import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Sparkles,
  User,
  Calendar,
  CreditCard,
  Camera,
  Settings,
  Gift,
  Star,
  X,
  Play,
  Scissors,
  DollarSign,
  Clock,
  Users
} from "lucide-react";
import { cn } from "@/lib/utils";

// Checklist items per role
const CHECKLIST_ITEMS = {
  client: [
    { 
      id: "profile", 
      label: "Complete your profile", 
      description: "Add your name and photo",
      icon: User, 
      path: "/dashboard",
      checkKey: "profile_complete"
    },
    { 
      id: "find_stylist", 
      label: "Find a stylist", 
      description: "Browse stylists near you",
      icon: Scissors, 
      path: "/find-stylists",
      checkKey: "viewed_stylists"
    },
    { 
      id: "book", 
      label: "Book your first appointment", 
      description: "Schedule a service",
      icon: Calendar, 
      path: "/book",
      checkKey: "first_booking"
    },
    { 
      id: "loyalty", 
      label: "Check your rewards", 
      description: "See your loyalty points",
      icon: Gift, 
      path: "/dashboard",
      checkKey: "viewed_loyalty"
    }
  ],
  stylist: [
    { 
      id: "profile", 
      label: "Complete your profile", 
      description: "Add bio, photo & specialties",
      icon: User, 
      path: "/stylist",
      checkKey: "profile_complete"
    },
    { 
      id: "services", 
      label: "Add your services", 
      description: "Set up pricing & duration",
      icon: DollarSign, 
      path: "/stylist",
      tab: "services",
      checkKey: "services_added"
    },
    { 
      id: "schedule", 
      label: "Set your availability", 
      description: "Define working hours",
      icon: Clock, 
      path: "/stylist",
      tab: "schedule",
      checkKey: "schedule_set"
    },
    { 
      id: "gallery", 
      label: "Upload portfolio photos", 
      description: "Showcase your work",
      icon: Camera, 
      path: "/stylist",
      tab: "gallery",
      checkKey: "gallery_uploaded"
    },
    { 
      id: "payments", 
      label: "Configure payment methods", 
      description: "CashApp, Zelle, Stripe",
      icon: CreditCard, 
      path: "/stylist",
      tab: "payments",
      checkKey: "payments_configured"
    }
  ],
  owner: [
    { 
      id: "salon", 
      label: "Set up your salon", 
      description: "Add name & location",
      icon: Settings, 
      path: "/admin",
      checkKey: "salon_setup"
    },
    { 
      id: "booth", 
      label: "Add booth renters", 
      description: "Register your stylists",
      icon: Users, 
      path: "/stylist",
      tab: "booth",
      checkKey: "booths_added"
    },
    { 
      id: "payments", 
      label: "Configure payments", 
      description: "Set up rent collection",
      icon: CreditCard, 
      path: "/stylist",
      tab: "payments",
      checkKey: "payments_configured"
    }
  ]
};

const OnboardingChecklist = ({ userRole = "client", userData = {}, onDismiss, compact = false }) => {
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [completedItems, setCompletedItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  const items = CHECKLIST_ITEMS[userRole] || CHECKLIST_ITEMS.client;

  // Load completed items from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`myhairchair_checklist_${userRole}`);
    if (stored) {
      setCompletedItems(JSON.parse(stored));
    }
    
    const isDismissed = localStorage.getItem(`myhairchair_checklist_dismissed_${userRole}`);
    if (isDismissed === "true") {
      setDismissed(true);
    }
  }, [userRole]);

  // Auto-check items based on user data
  useEffect(() => {
    const autoChecked = [];
    
    // Check profile completion
    if (userData?.name && userData?.email) {
      autoChecked.push("profile");
    }
    
    // Check if user has services (stylist)
    if (userData?.services?.length > 0) {
      autoChecked.push("services");
    }
    
    // Check if user has gallery photos
    if (userData?.gallery?.length > 0) {
      autoChecked.push("gallery");
    }
    
    // Check if user has appointments
    if (userData?.appointments?.length > 0) {
      autoChecked.push("book");
      autoChecked.push("first_booking");
    }

    if (autoChecked.length > 0) {
      setCompletedItems(prev => {
        const merged = [...new Set([...prev, ...autoChecked])];
        localStorage.setItem(`myhairchair_checklist_${userRole}`, JSON.stringify(merged));
        return merged;
      });
    }
  }, [userData, userRole]);

  const toggleItem = (itemId) => {
    setCompletedItems(prev => {
      const updated = prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];
      localStorage.setItem(`myhairchair_checklist_${userRole}`, JSON.stringify(updated));
      return updated;
    });
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(`myhairchair_checklist_dismissed_${userRole}`, "true");
    onDismiss?.();
  };

  const progress = Math.round((completedItems.length / items.length) * 100);
  const isComplete = completedItems.length === items.length;

  if (dismissed && !isComplete) {
    // Show mini reminder button
    return (
      <button
        onClick={() => {
          setDismissed(false);
          localStorage.removeItem(`myhairchair_checklist_dismissed_${userRole}`);
        }}
        className="fixed bottom-24 left-6 lg:bottom-6 lg:left-72 bg-gradient-to-r from-amber-500 to-orange-500 text-black px-4 py-2 rounded-full shadow-lg flex items-center gap-2 hover:scale-105 transition-transform z-40"
        data-testid="show-checklist-btn"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium">{completedItems.length}/{items.length} Setup</span>
      </button>
    );
  }

  if (isComplete) {
    return null; // Hide when complete
  }

  return (
    <div 
      className={cn(
        "bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl overflow-hidden transition-all duration-300",
        compact ? "mb-4" : "mb-6"
      )}
      data-testid="onboarding-checklist"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-amber-500/5 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-950 dark:text-white flex items-center gap-2">
              Get Started
              <span className="text-sm font-normal text-stone-500">
                {completedItems.length}/{items.length} complete
              </span>
            </h3>
            <Progress value={progress} className="h-1.5 w-32 mt-1" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            className="text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-stone-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-stone-500" />
          )}
        </div>
      </div>

      {/* Checklist Items */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-2">
          {items.map((item) => {
            const isChecked = completedItems.includes(item.id);
            return (
              <Link
                key={item.id}
                to={item.path}
                state={item.tab ? { activeTab: item.tab } : undefined}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-all",
                  isChecked 
                    ? "bg-emerald-500/10 border border-emerald-500/30" 
                    : "bg-white/50 dark:bg-stone-800/50 border border-transparent hover:border-amber-500/30 hover:bg-amber-500/5"
                )}
                onClick={() => !isChecked && toggleItem(item.id)}
                data-testid={`checklist-item-${item.id}`}
              >
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleItem(item.id);
                  }}
                  className={cn(
                    "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all",
                    isChecked 
                      ? "bg-emerald-500 border-emerald-500" 
                      : "border-stone-300 dark:border-stone-600 hover:border-amber-500"
                  )}
                >
                  {isChecked && <Check className="w-4 h-4 text-white" />}
                </button>
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                  isChecked ? "bg-emerald-500/20" : "bg-amber-500/20"
                )}>
                  <item.icon className={cn(
                    "w-4 h-4",
                    isChecked ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
                  )} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "font-medium text-sm",
                    isChecked ? "text-emerald-700 dark:text-emerald-400 line-through" : "text-emerald-950 dark:text-white"
                  )}>
                    {item.label}
                  </p>
                  <p className="text-xs text-stone-500 truncate">{item.description}</p>
                </div>
                {!isChecked && (
                  <Play className="w-4 h-4 text-amber-500 flex-shrink-0" />
                )}
              </Link>
            );
          })}
          
          {/* Watch Tutorials Link */}
          <Link
            to={`/user-guide?role=${userRole}`}
            className="flex items-center gap-2 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg hover:bg-violet-500/20 transition-colors"
          >
            <Play className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-medium text-violet-700 dark:text-violet-400">
              Watch video tutorials
            </span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
